<template>
  <div
    class="px-1 pt-2"
    :style="showModal ? 'min-height: calc(100vh - 700px) !important;' : ''"
  >
    <!-- Button to open modal to add a new method -->
    <div class="flex justify-end mb-1">
      <div class="relative">
        <button
          @click="toggleModal"
          class="self-settings-add-btn flex items-center text-xs"
        >
          <!-- <span class="text-lg mr-1">+</span>  -->
          <i class="fas fa-plus fa-xs mr-1"></i>
          Add {{reactivityName}}
        </button>

        <!-- Modal Dialog for Adding New Reactivity -->
        <div
          v-if="showModal"
          class="absolute right-full mr-2 top-0 bg-white border border-gray-200 rounded-lg shadow-md p-3 pb-2.5 z-10 w-64"
        >
          <!-- Reactivity Name Input -->
          <input
            v-model="newReactivity.name"
            ref="reactivityInput"
            type="text"
            placeholder="Enter Reactivity name"
            class="w-full mb-3 px-2 py-1 border rounded text-xs custom-focus"
          />
          <div class="flex justify-end space-x-2">
            <!-- Cancel Button -->
            <button
              @click="toggleModal"
              class="ml-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-1 px-2 rounded text-xs self-settings-cancel-btn"
            >
              Cancel
            </button>
            <!-- Add Reactivity Button -->
            <button
              @click="addReactivity"
              class="text-white font-bold py-1 px-2 rounded text-xs self-settings-add-btn"
              :class="
                !newReactivity.name
                  ? 'cursor-not-allowed bg-blue-300'
                  : 'bg-blue-400 hover:bg-blue-500'
              "
              :disabled="!newReactivity.name"
            >
              Add
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Displaying added reactivity -->
    <div v-for="(reactivity, index) in localReactivity" :key="index">
      <div class="rounded-lg py-2 flex flex-col gap-2">
        <!-- Reactivity Name Input Field -->
        <div class="flex items-center gap-3">
          <label class="text-gray-700 font-medium w-16 text-xs">Name:</label>
          <input
            v-model="reactivity.name"
            type="text"
            class="flex-grow px-3 py-1 border border-gray-300 custom-focus rounded-md"
            placeholder="Enter reactivity name"
            @input="updateReactivityData(index, 'name', $event.target.value)"
          />
        </div>
        <!-- Reactivity Body Input Field (Template Editor) -->
        <div class="flex items-center gap-4">
          <label class="text-gray-700 font-medium text-left ml-2.5 text-xs"
            >Body:</label
          >
          <div class="zoom-view-fixed w-full border rounded-md ml-3">
            <!-- Template Editor for Method Body -->
            <ZoomView v-slot="{ modal }">
              <div
                :style="
                  !modal ? 'height: 80px !important' : 'height: 100% !important'
                "
              >
                <TemplateEditor
                  :template="reactivity.body"
                  currentLanguage="javascript"
                  @set="(code) => updateReactivityBody(index, code)"
                />
              </div>
            </ZoomView>
          </div>
        </div>
        <!-- Remove Reactivity Button -->
        <button
          @click="removeReactivity(index)"
          class="text-gray-500 hover:text-red-500 self-end"
          aria-label="Remove Reactivity"
        >
          <i class="fas fa-trash fa-sm"></i>
        </button>
      </div>
    </div>

    <!-- Message when no Reactivity are added -->
    <div v-if="localReactivity.length === 0" class="opacity-50 pt-2">
      No {{ reactivityName }} added yet. Click the +Add button to add a new Reactivity.
    </div>
  </div>
</template>

<script>
import TemplateEditor from "./TemplateEditor.vue";
import ZoomView from "../ui/ZoomView.vue";

export default {
  name: "ReactivityComponent",
  props: {
    // The 'reactivity' prop receives an array of reactivity data
    reactivityName: {
      type: String,
      default: 'computed'
    },
    reactivity: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // 'localReactivity' stores a local copy of reactivity for easy editing
      localReactivity: [],
      // 'showModal' controls the visibility of the modal
      showModal: false,
      // 'newReactivity' holds the data for a new Reactivity being added
      newReactivity: { name: "", body: "" },
    };
  },
  components: {
    TemplateEditor,
    ZoomView,
  },
  methods: {
    /**
     * Toggles the visibility of the modal.
     * Resets the 'newReactivity' object if the modal is closed.
     */
    toggleModal() {
      this.showModal = !this.showModal;
      if (!this.showModal) {
        this.newReactivity = { name: "", body: "" };
      } else {
        this.$nextTick(() => {
          this.$refs.reactivityInput.focus();
        });
      }
    },

    /**
     * Adds a new reactivity to the list by emitting the 'add' event.
     * If the reactivity name is not empty, it sends the new reactivity name.
     */
    addReactivity() {
      if (this.newReactivity.name.trim()) {
        let name = this.newReactivity.name.trim();
        this.$emit("add", { name });
        this.toggleModal();
      }
    },

    /**
     * Removes a reactivity at a given index by emitting the 'remove' event.
     * A confirmation prompt is displayed before removing the reactivity.
     *
     * @param {number} index - Index of the reactivity to remove
     */
    removeReactivity(index) {
      if (window.confirm("Are you sure you want to remove this Reactivity?")) {
        this.$emit("remove", { index });
      }
    },

    /**
     * Updates a specific field of a reactivity based on the field name and value.
     * This reactivity is triggered when the user edits reactivity properties.
     *
     * @param {number} index - Index of the reactivity being edited
     * @param {string} field - The name of the field being updated (e.g., 'name')
     * @param {any} value - The new value for the field
     */
    updateReactivityData(index, field, value) {
      this.$emit("update", { key: field, index, value: value });
    },

    /**
     * Updates the body of a reactivity when edited using the TemplateEditor component.
     *
     * @param {number} index - Index of the reactivity being edited
     * @param {string} body - The new body content for the reactivity
     */
    updateReactivityBody(index, body) {
      this.$emit("update", { key: "body", index, value: body });
    },
  },
  watch: {
    /**
     * Watches for changes in the 'reactivity' prop and updates the 'localReactivity' array.
     *
     * @param {Array} newReactivity - The new array of reactivity passed from the parent
     */
    reactivity: {
      handler(newReactivity) {
        this.localReactivity = [...newReactivity];
      },
      immediate: true,
      deep: true,
    },
  },
  created() {
    /**
     * Initializes the 'localReactivity' array with the reactivity prop.
     */
    this.localReactivity = [...this.reactivity];
  },
};
</script>

<style scoped>
.self-settings-add-btn {
  font-weight: bold;
  transition: background-color 0.2s;
}

.zoom-view-fixed {
  height: 80px;
  overflow-y: auto;
}
</style>
